import React, { useEffect, useRef, useState } from 'react';
import { EntityIcon, isImageIcon } from './EntityIcon';
import { colorForId } from './MessageBubble';
import { IconPeople } from './Icons';
import './CollaboratorsBadge.css';

const MAX_AVATARS = 4;

function MemberAvatar({ member, status }) {
  const color = colorForId(member.userId);
  return (
    <span className="collab-avatar" title={member.name}>
      {member.avatar && isImageIcon(member.avatar) ? (
        <EntityIcon icon={member.avatar} name={member.name} size={22} />
      ) : member.avatar ? (
        <span className="collab-avatar-emoji">{member.avatar}</span>
      ) : (
        <span className="collab-avatar-initial" style={{ background: color }}>
          {(member.name || '?').charAt(0).toUpperCase()}
        </span>
      )}
      {status && <span className={`collab-avatar-dot ${status}`} aria-hidden="true" />}
    </span>
  );
}

// Fixed bottom-left "Collaborators" widget on the home (Today) screen: an
// overlapping-avatar pill listing who has joined the current workspace.
// Clicking it opens a small popover with the full roster (name + role) and,
// for admins, an "Invite" shortcut into the existing InvitePanel.
export default function CollaboratorsBadge({
  members = [],
  onlineUsers = {},
  isAdmin = false,
  onInvite,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!members.length) return null;

  const shown = members.slice(0, MAX_AVATARS);
  const overflow = members.length - shown.length;
  const label = `${members.length} collaborator${members.length === 1 ? '' : 's'}`;

  return (
    <div className="collab-badge-wrap" ref={wrapRef}>
      {open && (
        <div className="collab-popover" role="menu" aria-label="Workspace collaborators">
          <div className="collab-popover-title">{label}</div>
          <div className="collab-popover-list">
            {members.map((m) => (
              <div key={m.userId} className="collab-popover-row">
                <MemberAvatar member={m} status={onlineUsers[m.userId]?.status} />
                <span className="collab-popover-name">{m.name}</span>
                <span className={`collab-popover-role role-${m.role}`}>{m.role}</span>
              </div>
            ))}
          </div>
          {isAdmin && onInvite && (
            <button
              type="button"
              className="collab-popover-invite"
              onClick={() => {
                setOpen(false);
                onInvite();
              }}
            >
              <IconPeople size={13} /> Invite collaborators
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className="collab-badge"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        title={label}
      >
        <span className="collab-badge-avatars">
          {shown.map((m) => (
            <MemberAvatar key={m.userId} member={m} status={onlineUsers[m.userId]?.status} />
          ))}
          {overflow > 0 && <span className="collab-badge-more">+{overflow}</span>}
        </span>
      </button>
    </div>
  );
}
